import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { request } from '../../Utils/request';
import { ITrip } from './Trips';
import { TripData } from './TripCard';
import TripCard from './TripCard';
import { paiseToRupees } from '../../Utils/currencyConverter';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import ShareModal from '../../Components/ShareModal';

const UserTrips: React.FC = () => {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [trips, setTrips] = useState<ITrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedTripForShare, setSelectedTripForShare] = useState<TripData | null>(null);

  const userName = (location.state as { userName?: string })?.userName || 'Trips';

  useEffect(() => {
    const fetchUserTrips = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        const res = await request<{ success: boolean; data: ITrip[]; message?: string }>(
          `/trips/user/${userId}/trips`,
          {
            method: 'GET',
            authorization: true,
          }
        );
        setTrips(res?.data ?? []);
      } catch (err) {
        console.error('Error fetching user trips:', err);
        setTrips([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserTrips();
  }, [userId]);

  const handleTripClick = (trip: TripData) => {
    if (trip?.id) {
      const tripType = trip.type || 'trip';
      navigate(`/trips/${trip.id}?type=${tripType}`);
    }
  };

  const handleTripShare = (trip: TripData) => {
    const baseUrl =
      process.env.NODE_ENV === 'production'
        ? 'https://ifinds.in'
        : window.location.origin.replace('https://', 'http://');
    const tripType = trip.type?.toLowerCase() === 'itinerary' ? 'itinerary' : 'trip';
    const shareUrl = `${baseUrl}/trips/${trip.id}${tripType === 'itinerary' ? '?type=itinerary' : ''}`;
    setSelectedTripForShare({ ...trip, shareUrl });
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
    setSelectedTripForShare(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', pb: 2 }}>
      <TopNavBar title={userName} canGoBack onBackClick={() => navigate(-1)} />
      <Box sx={{ px: 2, pt: 2 }}>
        {trips.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Typography variant="body2" color="text.secondary">
              No trips found
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {trips.map((trip) => {
              const transformedTrip: TripData = {
                id: trip._id,
                title: trip.title,
                location: trip.location?.name || '',
                duration: `${trip.schedule?.dailySchedule?.length || 0} Days`,
                price: `₹${paiseToRupees(trip.pricePerPerson).toFixed(2)}`,
                imageUrl: trip.files?.[0]?.fileURL || '',
                category: trip.category?.[0] || '',
                type: (trip as any).type || 'Trip',
                status: (trip as any).status || '',
              };
              return (
                <TripCard
                  key={transformedTrip.id}
                  trip={transformedTrip}
                  variant="trips"
                  onClick={handleTripClick}
                  onShare={handleTripShare}
                />
              );
            })}
          </Stack>
        )}
      </Box>
      <ShareModal
        shareUrl={selectedTripForShare?.shareUrl || ''}
        open={shareModalOpen}
        onClose={handleShareModalClose}
        title={selectedTripForShare?.title || ''}
      />
    </Box>
  );
};

export default UserTrips;
